import type React from 'react';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Button } from 'flowbite-react';
import {
	DriveInviteeApplicationStatus,
	DriveInviteeDocument,
	EligibilityTestComponentProps,
	PaymentType,
	WeekDay,
} from 'types';
import { EligibilityDateSelection } from './EligibilityDateSelection';
import { EligibilityTimeSelection } from './EligibilityTimeSelection';
import { DayTimeSlot, SlotTime } from 'types';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import WhyTakeTest from './WhyTakeTest';
import { APP_ENDPOINT } from 'src/config/env.config';
import { RAZORPAY_APP_ID } from 'src/config/env.config';
import useRazorpay, { RazorpayOptions } from 'react-razorpay';
import { get } from 'lodash';
import { cancelPaymentById, completePaymentForDrive, createPaymentForDrive } from 'src/apis/payment';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { sendTrackingEvent } from 'src/utils/gtm';
import { useLocation } from 'react-router-dom';

dayjs.extend(utc);
dayjs.extend(timezone);

const EligibilityTestComponent: React.FC<
	EligibilityTestComponentProps & { userApplicationDetails?: DriveInviteeDocument['userApplicationDetails'] }
> = ({
	headerTitle,
	description,
	isLoading,
	slots = [],
	note,
	shouldTimeZoneConvert = false,
	refundable = true,
	qualifierFee,
	currency = '₹',
	driveApplicationId,
	userApplicationDetails,
}) => {
	const toast = useToast();
	const Razorpay = useRazorpay();
	const location = useLocation();
	const queryClient = useQueryClient();
	const { user: session } = useUserContext();
	const [selectedDate, setSelectedDate] = useState<string | null>(null);
	const [userTimeZone, setUserTimeZone] = useState<string>('Asia/Kolkata');
	const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
	const [selectedDaySlot, setSelectedDaySlot] = useState<DayTimeSlot>();
	const [selectedTimeSlot, setSelectedTimeSlot] = useState<SlotTime>();

	const timezones =
		dayjs.tz.guess() !== 'Asia/Kolkata' && dayjs.tz.guess() !== 'Asia/Calcutta'
			? [
					{
						value: 'Asia/Calcutta',
						label: `${'Asia/Calcutta'} (${dayjs().tz('Asia/Kolkata').format('Z')})`,
					},
					{
						value: dayjs.tz.guess(),
						label: `${dayjs.tz.guess()} (${dayjs().tz(dayjs.tz.guess()).format('Z')})`,
					},
			  ]
			: [
					{
						value: 'Asia/Calcutta',
						label: `${'Asia/Calcutta'} (${dayjs().tz('Asia/Calcutta').format('Z')})`,
					},
			  ];
	useEffect(() => {
		if (slots?.length > 0) {
			setSelectedDaySlot(slots[0]);
		}
	}, [slots]);

	const cancelPaymentFn = useMutation(['cancelPaymentById'], cancelPaymentById, {
		onSuccess() {
			toast({
				title: 'Payment Failed.',
				type: 'error',
			});
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const completePaymentForDriveMutation = useMutation(['completePaymentForDrive'], completePaymentForDrive, {
		onSuccess() {
			queryClient.invalidateQueries(['getDriveRegistrationApplication']);
			toast({
				title: 'Payment Done Successfully.',
				type: 'success',
			});
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const handleCompletePayment = (paymentId?: string) => {
		if (selectedDaySlot) {
			completePaymentForDriveMutation.mutate({
				driveApplicationId: driveApplicationId || '',
				selectedTestSlot: {
					startTime: dayjs(selectedTimeSlot?.time || '').toDate(),
					endTime: dayjs(selectedTimeSlot?.time || '')
						.add(Number(selectedDaySlot?.duration || 0), 'minutes')
						.toDate(),
					duration: Number(selectedDaySlot?.duration || 0),
					day: selectedDaySlot?.day || WeekDay.Monday,
				},
				paymentId: paymentId,
			});
		}
	};

	const createPaymentForDriveMutation = useMutation(['createPaymentForDrive'], createPaymentForDrive, {
		onSuccess(data, variables) {
			const finalPrice = data.finalAmount;
			const paymentId = data.payment?._id || '';
			console.log('session:', session);

			if (finalPrice != null && finalPrice > 0) {
				const options: RazorpayOptions = {
					key: RAZORPAY_APP_ID,
					amount: String(finalPrice),
					currency: 'INR',
					name: 'LevelUp',
					description: `Transaction to buy a Service (Drive Registration)`,
					image: `https://masai-website-images.s3.ap-south-1.amazonaws.com/svgviewer_png_output_8_a90c76ce97.png`,
					callback_url: `${APP_ENDPOINT}/drive-registration-application/${driveApplicationId}`,
					order_id: data.payment?.orderId || '',
					handler: () => {
						handleCompletePayment(paymentId);
					},
					prefill: {
						name: session?.name,
						email: session?.email,
						contact: session?.phone || '',
					},
					notes: {
						address: 'LevelUp Corporate Office',
					},
					theme: {
						color: '#7777E7',
					},
					modal: {
						ondismiss: (() => {
							cancelPaymentFn.mutate(paymentId);
						}) as unknown as boolean,
					},
				};
				const rzpay = new Razorpay(options);
				rzpay.on('payment.failed', function () {
					cancelPaymentFn.mutate(paymentId);
				});
				rzpay.open();
			} else if (data) {
				if (paymentId) {
					handleCompletePayment(paymentId);
				}
			} else {
				toast({
					title: 'Payment creation failed',
					type: 'error',
				});
			}
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const handleCreatePayment = () => {
		if (qualifierFee === 0) {
			handleCompletePayment();
		} else if (userApplicationDetails?.status === DriveInviteeApplicationStatus.PAYMENT_SUCCESS) {
			handleCompletePayment(
				userApplicationDetails?.payments?.find(payment => payment.type === PaymentType.Assessment.toString())?._id ||
					userApplicationDetails?.payments?.[0]?._id ||
					'',
			);
		} else if (driveApplicationId) {
			createPaymentForDriveMutation.mutate({
				driveApplicationId: driveApplicationId || '',
				meta: {
					type: PaymentType.Assessment,
					driveApplicationId: driveApplicationId,
					selectedTestSlot: {
						startTime: dayjs(selectedTimeSlot?.time || '').toDate(),
						endTime: dayjs(selectedTimeSlot?.time || '')
							.add(Number(selectedDaySlot?.duration || 0), 'minutes')
							.toDate(),
						duration: Number(selectedDaySlot?.duration || 0),
						day: selectedDaySlot?.day || WeekDay.Monday,
					},
				},
			});
			sendTrackingEvent({
				event: 'recr_dashboard_active_companies',
				eventLabel: location.pathname,
			});
		} else {
			toast({
				type: 'error',
				title: 'Drive application id is required',
			});
		}
	};

	return (
		<div className="lg:max-w-587px h-full flex flex-col">
			{/* Header */}
			<h5 className="mb-1 text-[#0A0103] text-[18px] font-[700] leading-[24px]">{headerTitle}</h5>
			{shouldTimeZoneConvert && (
				<div className="mb-5 mt-3 max-w-500px">
					<label className="block mb-2">Select Timezone</label>
					<select
						value={userTimeZone || 'Asia/Kolkata'}
						onChange={e => {
							if (!setUserTimeZone) return;
							setUserTimeZone(e.target.value);
						}}
						className="w-full p-3 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer hover:border-gray-400 transition-colors"
					>
						{timezones.map(tz => (
							<option key={tz.value} value={tz.value} className="py-2">
								{tz.label}
							</option>
						))}
					</select>
				</div>
			)}

			<div className="text-[14px] font-inter leading-[24px] font-[600] text-[#544D4F]">{description}</div>

			<div className="mb-4 md:mb-4 mt-4 md:mt-6 pb-10 md:pb-0">
				{/* Choose Slot Section */}
				{slots?.length > 0 ? (
					<div className="flex flex-col gap-4">
						<EligibilityDateSelection
							allSlots={slots}
							selectedDaySlot={selectedDaySlot}
							setSelectedDaySlot={(value: DayTimeSlot) => {
								setSelectedDaySlot(value);
								setSelectedTimeSlot(undefined);
							}}
						/>
						<EligibilityTimeSelection
							selectedDaySlot={selectedDaySlot}
							selectedTimeSlot={selectedTimeSlot}
							setSelectedTimeSlot={setSelectedTimeSlot}
							heading="Choose Your Slot"
						/>
					</div>
				) : (
					<div className="flex-1 flex items-center justify-center h-full text-center py-8">
						<div className="flex flex-col items-center justify-center">
							<img
								src={'https://masai-website-images.s3.ap-south-1.amazonaws.com/svgviewer_png_output_33_bf62a13130.png'}
								className="max-h-[90px] md:max-h-[150px]"
								alt="No Slots"
							/>
							<h6 className="mt-2 text-base md:text-lg font-[700] leading-[20px] text-[#544D4F] font-poppins">
								No Slots Found!
							</h6>
							<p className="text-xs md:text-sm text-gray-500 font-poppins mt-1 max-w-[250px] md:max-w-[320px]">
								No slots found for this position. Please check back later.
							</p>
						</div>
					</div>
				)}

				{/* <div className="flex gap-1 items-center">
					<WhyTakeTest />
				</div> */}
			</div>

			{/* Footer Section */}
			<div className="mt-auto pt-2 fixed px-2 pb-1 lg:px-0 lg:sticky bottom-0 w-full inset-x-0 bg-[#FBFBFB] border-t tlg:border-t-0 text-center">
				<Button
					size="lg"
					className="w-full uppercase font-[600]"
					isProcessing={
						isLoading ||
						createPaymentForDriveMutation?.isLoading ||
						completePaymentForDriveMutation?.isLoading ||
						cancelPaymentFn?.isLoading
					}
					onClick={() => {
						if (dayjs().isAfter(dayjs(selectedTimeSlot?.time || '').tz('Asia/Kolkata'))) {
							return toast({
								type: 'error',
								title: 'Test slot is expired',
							});
						}
						console.log('selectedTimeSlot:', { selectedDaySlot, selectedTimeSlot });
						if (selectedTimeSlot) {
							handleCreatePayment();
						}
					}}
					color="purplePlaced"
					disabled={
						!selectedTimeSlot ||
						createPaymentForDriveMutation?.isLoading ||
						completePaymentForDriveMutation?.isLoading ||
						cancelPaymentFn?.isLoading
					}
				>
					{userApplicationDetails?.status === DriveInviteeApplicationStatus.PAYMENT_SUCCESS || qualifierFee === 0
						? 'Schedule'
						: `Pay ${currency || '₹'}${qualifierFee || '99'} and Schedule`}
				</Button>
				<p className="mt-2 text-[#3E7BFA] font-openSans italic text-left text-sm md:text-base">
					{refundable ? note : null}
				</p>
			</div>
		</div>
	);
};

export default EligibilityTestComponent;
